/// <reference types="node" />
import { CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
  AnonymousUserContext,
  LinqFeatureFlagKnownUserContext,
  LinqFeatureFlagMultiContext,
  LinqFeatureFlagService,
} from '@linq/feature-flag';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  pairwise,
  take,
  takeUntil,
  throttleTime,
  withLatestFrom,
  zip,
} from 'rxjs';
import {
  AuthService,
  ConfigService,
  Frames,
  FramesService,
  LoggerService,
  SessionActivityService,
} from '@app/core/services';
import { PendingLaunchService } from '@app/core/services/pending-launch/pending-launch.service';
import { LaunchV4PageService } from '@app/core/services/launch-v4-page/launch-v4-page.service';
import { FeatureFlagEnum } from '@app/enums';
import { FramesComponent } from '@app/core/components';
import { InactivityWarningComponent } from '@app/core/components/inactivity-warning/inactivity-warning.component';
import { LoadingComponent } from './shared/components/loading';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

/**
 * ERP-15430 — minimum interval (ms) between two
 * `POST /session/heartbeat` calls. The cookie's 90-minute sliding
 * window doesn't need to be reset more often than once a minute, so we
 * throttle activity events to one network round-trip per minute.
 */
const HEARTBEAT_THROTTLE_MS = 60_000;

/**
 * ERP-15430 — how many seconds before the shared session's expiry V2 pops
 * the inactivity warning. Driven by the heartbeat poll's `secondsRemaining`
 * (the SHARED cookie clock, refreshed by activity in either app), so the
 * warning only shows when the real cross-app session is near expiry.
 */
const WARNING_THRESHOLD_SECONDS = 120;

declare global {
  interface Window {
    bs_init: (options: any) => void;
    Frames: Frames;
    $: any;
    authService: AuthService;
    FeatureFlag: LinqFeatureFlagService;
    framesService: FramesService;
  }
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FramesComponent, LoadingComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private readonly framesService: FramesService,
    public readonly authService: AuthService,
    private readonly configService: ConfigService,
    private readonly featureFlagService: LinqFeatureFlagService,
    private readonly loggerService: LoggerService,
    private readonly sessionActivityService: SessionActivityService,
    private readonly pendingLaunchService: PendingLaunchService,
    private readonly ngZone: NgZone,
    private readonly dialog: MatDialog,
    private readonly launchV4PageService: LaunchV4PageService,
  ) {}

  /**
   * Open cross-app inactivity warning dialog (ERP-15430), or undefined when
   * none is showing. Driven by the heartbeat poll's shared-cookie TTL.
   */
  private warningDialogRef: MatDialogRef<InactivityWarningComponent> | undefined;

  /**
   * Lifetime signal for the cross-app session-activity wiring (ERP-15430).
   * Separate from the per-active-session subscription so we can tear down
   * the DOM activity listeners on app destroy without leaking.
   */
  private readonly destroy$ = new Subject<void>();

  /**
   * Subscription to the "actually post heartbeats now" stream. Created
   * when `v2-v4-session-logout` is on AND an access token is available;
   * nulled when either flips false. Storing it lets us tear down without
   * affecting other subscriptions.
   */
  private sessionActivitySubscription: Subscription | undefined;

  /**
   * Whether the one-time bootstrap heartbeat has already been sent this app
   * session. The bootstrap `POST { bootstrap: true }` UNCONDITIONALLY (re)issues
   * the shared cookie with a fresh 90-min window, so it must fire only ONCE — on
   * the first engage after login. Re-engagements (notably a transient
   * `hasAccessToken$` blip during the scheduled Auth0 token refresh) must NOT
   * re-bootstrap, or they'd reset the inactivity window with no user activity.
   * ERP-15430.
   */
  private hasBootstrapped = false;

  private menuOpenedSubject = new BehaviorSubject<boolean>(false);
  public menuOpened$ = this.menuOpenedSubject.asObservable();

  public isFrames$ = this.framesService.isFrames$.asObservable();

  public isLoading$ = zip(this.authService.isLoading$, this.featureFlagService.ready$).pipe(
    map(([isLoading, ready]) => {
      return isLoading && !ready;
    }),
  );

  private sessionReplayRecordingEnabled$ = this.featureFlagService.flag$(FeatureFlagEnum.DD_SessionReplayRecording);

  ngOnInit(): void {
    this.loggerService.initializeLogging();
    const build = `BUILD: ${process.env['BUILD_ID']} \nBUILD DATE: ${new Date(
      String(process.env['BUILD_DATE']),
    ).toLocaleString()}`;
    // eslint-disable-next-line no-console
    console.log(build);
    this.featureFlagService.initialize(this.buildAnonymousContext());
    window.FeatureFlag = this.featureFlagService;

    // ERP-15430 cross-app launch (V4 → V2): capture `?callForm=` and arm
    // the dispatcher that waits for login + Frames readiness before
    // calling `Frames.Application.callmenu`. Replaces the prior
    // `/Foundations/LaunchForm` server-side bridge.
    this.pendingLaunchService.capture();
    this.pendingLaunchService.start();
    // Also listen for V4 postMessage launches into this already-open tab, so
    // subsequent form launches from V4 open in place without reloading V2.
    this.pendingLaunchService.listen();

    // ERP-15430 (reverse direction): shim the Frames `window.open(url,
    // "LINQ ERP V4")` launch so a V4 page opened FROM V2 reuses the existing
    // V4 tab via postMessage (in-place router nav) instead of re-navigating it.
    // Prevents the reload cascade where a V2→V4 launch reloads V4, wiping its
    // retained V2 handle and forcing the next V4→V2 launch to reload V2 too.
    this.launchV4PageService.install();

    // ----------------------------START Angular Menu---------------------------
    // this.framesService.menu$.subscribe((menu) => {
    //   const multilevelNodeMenu = convertToMultilevelNode(menu);
    //   this.menuService.setMenu(multilevelNodeMenu);

    //   // TODO: Enable Angular Menu Flag?
    //   this.framesService.interceptMenuToggle();
    // });

    // this.framesService.menuClicked$.subscribe(() => {
    //   this.menuService.toggle();
    // });
    // ----------------------------END Angular Menu---------------------------

    this.authService.isEnabled$.pipe(filter(Boolean), take(1)).subscribe(() => {
      // user clicks logout
      this.framesService.request$
        .pipe(
          filter((message) => {
            if (message?.header?.[0]?.control?.[0]?.action?.length > 0) {
              const action = message.header[0].control[0].action[0];
              const name = action['@name'];
              const item = action['@item'];
              const block = action['@block'];
              if (name == 'EXIT' && item == 'MENU_TREE' && block == '$MAIN$_BLOCK') {
                return true;
              }
            }
            return false;
          }),
          withLatestFrom(this.authService.isAuthenticated$),
        )
        .subscribe(([, authenticated]) => {
          if (authenticated) {
            this.authService.logout();
          }
        });

      this.authService.isAuthenticated$.pipe(pairwise()).subscribe(([previous, current]) => {
        if (previous && !current) {
          this.framesService.execute('auth0-logged-out');
        }
      });
    });

    // Combine the primary user stream (identity from Auth0 when enabled, else Frames) with
    // the Frames-only stream (for the ERP role). The .NET backend ships Globals.QtsiRoleId
    // as `role` — that's the user's current ERP/Oracle role (e.g. "SYS"), not the Auth0
    // admin claim ("super"). We mirror that on the browser by preferring framesUser.role
    // (== USER_ROLE) when available.
    combineLatest([this.authService.user$, this.authService.framesUser$])
      .pipe(
        // Gate: don't publish a LD context for an Auth0 user until framesUser is in,
        // otherwise we race two identify() calls (first with role='' then with role='SYS')
        // and the earlier one can win on the network -- leaving LD with role=''.
        // Anonymous (user==null) and Frames-only flows are unaffected.
        filter(([user, framesUser]) => !user || !user.isAuth0 || !!framesUser),
        // Dedupe so we don't re-identify on every emission when nothing relevant changed.
        distinctUntilChanged(
          ([prevUser, prevFrames], [currUser, currFrames]) =>
            prevUser?.id === currUser?.id && prevUser?.name === currUser?.name && prevFrames?.role === currFrames?.role,
        ),
      )
      .subscribe(([user, framesUser]) => {
        if (user) {
          const ldKey = user.isAuth0 && user.sub ? user.sub : `${user.subdomain ?? ''}-${user.dbUserId ?? ''}`;
          const erpRole = framesUser?.role || user.role || '';
          const knownUser: LinqFeatureFlagKnownUserContext = {
            kind: 'user',
            anonymous: false,
            key: ldKey,
            email: user.email,
            name: user.name,
            dbUserId: user.dbUserId ?? '',
            tenantId: user.tenantId ?? '',
            role: erpRole,
            version: 'v2',
            appName: 'erpv2',
            serverFlag: false,
            url: window.location.href.toLowerCase(),
            subdomain: user.subdomain ?? '',
            domain: window.location.hostname,
          };
          const context: LinqFeatureFlagMultiContext = {
            kind: 'multi',
            user: knownUser,
          };
          this.featureFlagService.setContext(context);
          const loggerUser = {
            id: user.id,
            email: user.email,
            name: user.name,
          };
          this.loggerService.setUser(loggerUser);
        } else {
          this.featureFlagService.setContext(this.buildAnonymousContext());
          this.loggerService.clearUser();
        }
      });

    this.sessionReplayRecordingEnabled$.subscribe((enabled) => {
      if (enabled) {
        this.loggerService.startRecording();
      } else {
        this.loggerService.stopRecording();
      }
    });

    this.initSessionActivitySync();
  }

  public ngOnDestroy(): void {
    this.stopSessionActivitySync();
    this.pendingLaunchService.stop();
    this.launchV4PageService.uninstall();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Build the anonymous (logged-out) LaunchDarkly context. `tenantId`
   * and `subdomain` are sourced from the app-config via `ConfigService`,
   * which doesn't depend on a user being identified — so tenant- and
   * subdomain-targeted flags can resolve correctly even on the pre-login
   * / Frames-only context.
   */
  private buildAnonymousContext() {
    return {
      ...AnonymousUserContext,
      user: {
        ...AnonymousUserContext.user,
        appName: 'erpv2',
        version: 'v2',
        url: window.location.href.toLowerCase(),
        domain: window.location.hostname,
        serverFlag: false,
        tenantId: this.configService.get('tenantId') ?? '',
        subdomain: this.resolveSubdomain(),
      },
    };
  }

  /**
   * Resolve the subdomain for the anonymous context: prefer the
   * app-config value, and fall back to parsing it from the current
   * hostname when config is empty. The parse mirrors `main.ts` — drop
   * the final two labels (domain + TLD) and join the rest — and yields
   * '' for hosts without a subdomain (e.g. `localhost`, `example.com`).
   */
  private resolveSubdomain(): string {
    const fromConfig = this.configService.get('auth0Subdomain');
    if (fromConfig) {
      return fromConfig;
    }
    const host = window.location.hostname.split('.');
    return host.length >= 2 ? host.slice(0, host.length - 2).join('.') : '';
  }

  /**
   * ERP-15430 — coordinator for the cross-app session-activity cookie.
   *
   * V2 participates in V4's inactivity tracking only when the
   * `v2-v4-session-logout` LaunchDarkly flag is on AND the user has an
   * Auth0 access token (V2 is authenticated against V4's identity
   * provider). When both signals are true, we:
   *   - Fire one bootstrap `POST /session/heartbeat` so the cookie
   *     exists from the moment V2 finishes authenticating (handles the
   *     "user signed into V2 first" case).
   *   - Start a 30-second `GET /session/heartbeat` poll. A 401 from the
   *     poll means the cookie disappeared (logout-from-V4 or 90-minute
   *     idle in both apps) — react by triggering V2's own logout flow.
   *   - Attach DOM activity listeners that, throttled to ≤1 call per
   *     minute, refresh the cookie via `POST` so the sliding window
   *     keeps sliding while the user is engaged in V2.
   *
   * When either signal flips back to false (flag turned off mid-session
   * or user logged out), we tear down the poll and the activity
   * subscription. Re-evaluation runs through `combineLatest` so any
   * transition is handled idempotently.
   */
  private initSessionActivitySync(): void {
    combineLatest([this.authService.isSessionLogoutEnabled$, this.authService.hasAccessToken$])
      .pipe(
        // Collapse to a single boolean and dedupe: LaunchDarkly re-pushing the
        // same flag value, or the token stream re-emitting, shouldn't churn the
        // sync machinery. Genuine transitions still start/stop it. ERP-15430.
        map(([flagOn, hasToken]) => flagOn && hasToken),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((shouldSync) => {
        if (shouldSync) {
          this.startSessionActivitySync();
        } else {
          this.stopSessionActivitySync();
        }
      });
  }

  /**
   * Idempotent start — guards against the combineLatest above firing
   * twice with the same shape on rapid flag/token churn.
   */
  private startSessionActivitySync(): void {
    if (this.sessionActivitySubscription) return;

    // Bootstrap the cookie immediately so the first GET poll (30s out)
    // doesn't see a missing cookie and treat it as a logout signal.
    // Explicit `bootstrap: true` so the V4 backend creates the cookie
    // even when one isn't present yet. The activity heartbeat below
    // intentionally omits this flag — server-side it then only refreshes
    // an existing cookie, never resurrecting one V4 just cleared.
    //
    // ONLY on the first engage. A re-engage (e.g. a transient
    // `hasAccessToken$` blip during Auth0 token refresh at ~5 min) must not
    // re-bootstrap — that would reset the shared inactivity window with no
    // user activity. The existing cookie's TTL is left untouched; the poll +
    // activity listeners below simply resume. ERP-15430.
    if (!this.hasBootstrapped) {
      this.sessionActivityService.postHeartbeat({ bootstrap: true }).subscribe();
      this.hasBootstrapped = true;
    }

    // 30-second poll. `onTick` carries the shared cookie's remaining lifetime
    // and drives the inactivity WARNING off the shared clock; `onUnauthorized`
    // (401) triggers V2's own logout flow.
    this.sessionActivityService.startPolling({
      onUnauthorized: () => {
        // eslint-disable-next-line no-console
        console.warn('🔌 Cross-app session ended (heartbeat returned 401) - logging out');
        this.closeWarningDialog();
        this.authService.logout({ skipSessionEnd: true });
      },
      onTick: (secondsRemaining) => this.handleSessionTick(secondsRemaining),
    });

    // Throttled activity heartbeat. Listen outside Angular's zone so a
    // continuous mousemove stream doesn't trigger change detection.
    this.sessionActivitySubscription = this.ngZone.runOutsideAngular(() =>
      merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart'),
      )
        .pipe(
          throttleTime(HEARTBEAT_THROTTLE_MS, undefined, { leading: true, trailing: false }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.sessionActivityService.postHeartbeat().subscribe();
        }),
    );
  }

  /** Idempotent stop — safe to call when not started. */
  private stopSessionActivitySync(): void {
    this.sessionActivityService.stopPolling();
    this.closeWarningDialog();
    if (this.sessionActivitySubscription) {
      this.sessionActivitySubscription.unsubscribe();
      this.sessionActivitySubscription = undefined;
    }
  }

  /**
   * Cross-app inactivity warning driver (ERP-15430) — invoked on every
   * successful heartbeat poll with the SHARED session's remaining lifetime.
   *
   *   - `null`       → transient read failure; ignore.
   *   - `<= 0`       → shared session expired; log out (backstop — a 401 from
   *                    the poll usually fires first).
   *   - `<= warning` → open the warning dialog counting down the real
   *                    remaining time, unless one is already showing.
   *   - `>  warning` → the session was refreshed (activity in this or the peer
   *                    app), so dismiss any warning that's currently up.
   */
  private handleSessionTick(secondsRemaining: number | null): void {
    if (secondsRemaining === null) {
      return;
    }

    if (secondsRemaining <= 0) {
      this.closeWarningDialog();
      this.authService.logout({ skipSessionEnd: true });
    } else if (secondsRemaining <= WARNING_THRESHOLD_SECONDS) {
      this.openWarningDialog(secondsRemaining);
    } else if (this.warningDialogRef) {
      this.closeWarningDialog();
    }
  }

  /**
   * Open the inactivity warning counting down `secondsRemaining`. No-op if a
   * warning is already showing. On close: "Stay" / auto-refresh → refresh the
   * shared cookie; "Logout Now" → log out; countdown-expiry ('timeout') →
   * verify the shared cookie first (a peer app may have kept it alive) and
   * only log out if it's genuinely dead.
   */
  private openWarningDialog(secondsRemaining: number): void {
    if (this.warningDialogRef) {
      return;
    }

    this.warningDialogRef = this.dialog.open(InactivityWarningComponent, {
      data: { countdownSeconds: secondsRemaining },
      disableClose: true,
      width: '500px',
    });

    this.warningDialogRef.afterClosed().subscribe((result) => {
      this.warningDialogRef = undefined;

      if (result === 'logout') {
        this.authService.logout();
      } else if (result === 'timeout') {
        this.sessionActivityService.checkSessionAlive().subscribe((alive) => {
          if (alive) {
            // Peer app kept the shared session alive — stay logged in and
            // refresh so our next poll sees the extended TTL.
            this.sessionActivityService.postHeartbeat().subscribe();
          } else {
            this.authService.logout({ skipSessionEnd: true });
          }
        });
      } else if (result === 'stay') {
        // 'stay' — refresh the shared cookie so the TTL clock resets cross-app.
        this.sessionActivityService.postHeartbeat().subscribe();
      }
    });
  }

  /** Close the inactivity warning if one is open. Idempotent. */
  private closeWarningDialog(): void {
    if (this.warningDialogRef) {
      this.warningDialogRef.close();
      this.warningDialogRef = undefined;
    }
  }

  public closeMenu(): void {
    this.menuOpenedSubject.next(false);
  }
}
